/**
 * CompanyLogoUpload Component
 *
 * Componente para upload, preview e remoção de logo da empresa
 */

import { useRef, useState } from 'react';
import { Upload, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCompanyLogo } from '@/hooks/useCompanyLogo';
import { cn } from '@/lib/utils';

export interface CompanyLogoUploadProps {
  companyId: string;
  companyName: string;
  currentLogoUrl?: string | null;
  onLogoChange: (newLogoUrl: string | null) => void;
  isEditing: boolean;
}

export function CompanyLogoUpload({
  companyId,
  companyName,
  currentLogoUrl,
  onLogoChange,
  isEditing,
}: CompanyLogoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentLogoUrl || null);
  const { uploadLogo, deleteLogo, isUploading, isDeleting } = useCompanyLogo();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Preview local before upload
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to Supabase
    uploadLogo({
      companyId,
      file,
      onSuccess: (url) => {
        setPreviewUrl(url);
        onLogoChange(url);
      },
      onError: () => {
        // Revert to previous logo on error
        setPreviewUrl(currentLogoUrl || null);
      },
    });

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!currentLogoUrl) return;

    try {
      await deleteLogo(companyId, currentLogoUrl);
      setPreviewUrl(null);
      onLogoChange(null);
    } catch (error) {
      console.error('Failed to delete logo:', error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const getInitials = () => {
    return companyName ? companyName[0].toUpperCase() : 'E';
  };

  const isLoading = isUploading || isDeleting;

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Logo Preview */}
      <div className="relative">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Logo da empresa"
            className={cn(
              "w-20 h-20 rounded-lg object-cover border-2 border-border",
              isLoading && "opacity-50"
            )}
          />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center text-3xl font-bold text-muted-foreground border-2 border-border">
            {getInitials()}
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-lg">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
      </div>

      {/* Upload/Delete Controls */}
      {isEditing && (
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isLoading}
          />

          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleUploadClick}
            disabled={isLoading}
            className="gap-1"
          >
            <Upload className="h-3 w-3" />
            {previewUrl ? 'Alterar' : 'Upload'}
          </Button>

          {previewUrl && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleRemoveLogo}
              disabled={isLoading}
              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3 w-3" />
              Remover
            </Button>
          )}
        </div>
      )}

      {/* File Info */}
      {isEditing && (
        <p className="text-xs text-muted-foreground text-center max-w-[160px]">
          JPG, PNG, WEBP ou SVG
          <br />
          Máx: 5MB
        </p>
      )}
    </div>
  );
}
